import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  CalendarMonthOutlined,
  SchoolOutlined,
  ExpandMoreOutlined,
  ExpandLessOutlined,
  CloseOutlined,
  AddOutlined,
  ReplayOutlined,
  DeleteOutlineOutlined,
  EditOutlined,
  ImageOutlined
} from '../../icons';
import StudyPlanCalendar, { DAYS } from '../../components/StudyPlanCalendar/StudyPlanCalendar';
import AutocompleteField from '../../components/AutocompleteField';
import { SUBJECT_COLOR_PALETTE } from '../../utils/subjectColors';
import { useCourse } from '../../contexts/CourseContext';
import DashboardTopbar from '../../components/DashboardTopbar';
import PlansModal from '../../components/PlansModal';
import './SubjectSelection.css';

const apiUrl = process.env.REACT_APP_API_URL;

const PRIORITIES = ['alta', 'media', 'baixa'];
const PRIORITY_LABELS = { alta: 'Alta', media: 'Média', baixa: 'Baixa' };
const PRIORITY_BAR_COUNT = { baixa: 1, media: 2, alta: 3 };

function SubjectSelection() {
  const navigate = useNavigate();
  const { activeCourseId, activeCourse, loadingCourses, updateCustomCourse, refreshCourses } = useCourse();
  const [courseNameDraft, setCourseNameDraft] = useState('');
  const [courseImageUrlDraft, setCourseImageUrlDraft] = useState('');
  const [editingCourseImageUrl, setEditingCourseImageUrl] = useState(false);
  const [courseExamDateDraft, setCourseExamDateDraft] = useState('');

  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [catalogSubjects, setCatalogSubjects] = useState([]);
  const [removedSubjectIds, setRemovedSubjectIds] = useState(() => new Set());
  const [priorities, setPriorities] = useState({});
  const [expandedSubjectIds, setExpandedSubjectIds] = useState(() => new Set());

  const [hoursPerDay, setHoursPerDay] = useState(2);
  const [selectedDays, setSelectedDays] = useState([]);

  const [newSubjectName, setNewSubjectName] = useState('');
  const [addingSubject, setAddingSubject] = useState(false);
  const [addSubjectError, setAddSubjectError] = useState('');

  const [addTopicFormSubjectId, setAddTopicFormSubjectId] = useState(null);
  const [newTopicName, setNewTopicName] = useState('');
  const [addingTopic, setAddingTopic] = useState(false);
  const [addTopicError, setAddTopicError] = useState('');

  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPlansModal, setShowPlansModal] = useState(false);

  const [previewPlan, setPreviewPlan] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [courseImageFailed, setCourseImageFailed] = useState(false);
  useEffect(() => {
    setCourseImageFailed(false);
  }, [activeCourse?.image_url]);

  useEffect(() => {
    setCourseNameDraft(activeCourse?.name || '');
    setCourseImageUrlDraft(activeCourse?.image_url || '');
    setEditingCourseImageUrl(false);
    setCourseExamDateDraft(activeCourse?.exam_date || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCourseId]);

  const saveCourseName = async () => {
    const name = courseNameDraft.trim();
    if (!name || name === activeCourse?.name) {
      setCourseNameDraft(activeCourse?.name || '');
      return;
    }
    try {
      await updateCustomCourse(activeCourseId, name, activeCourse?.description || '', activeCourse?.image_url || '', activeCourse?.exam_date || '');
    } catch (error) {
      console.error('Erro ao renomear concurso:', error);
      setCourseNameDraft(activeCourse?.name || '');
    }
  };

  const saveCourseImageUrl = async () => {
    const imageUrl = courseImageUrlDraft.trim();
    setEditingCourseImageUrl(false);
    if (imageUrl === (activeCourse?.image_url || '')) return;
    try {
      await updateCustomCourse(activeCourseId, activeCourse?.name || '', activeCourse?.description || '', imageUrl, activeCourse?.exam_date || '');
    } catch (error) {
      console.error('Erro ao atualizar imagem do concurso:', error);
      setCourseImageUrlDraft(activeCourse?.image_url || '');
    }
  };

  const saveCourseExamDate = async () => {
    const examDate = courseExamDateDraft;
    if (examDate === (activeCourse?.exam_date || '')) return;
    try {
      await updateCustomCourse(activeCourseId, activeCourse?.name || '', activeCourse?.description || '', activeCourse?.image_url || '', examDate);
    } catch (error) {
      console.error('Erro ao atualizar data da prova do concurso:', error);
      setCourseExamDateDraft(activeCourse?.exam_date || '');
    }
  };

  useEffect(() => {
    // Espera o CourseContext resolver o concurso ativo a partir do banco
    // antes de decidir (não é mais salvo no localStorage, então não está
    // disponível de cara no primeiro render).
    if (loadingCourses) return;

    if (!activeCourseId) {
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const [editalResponse, planResponse] = await Promise.all([
          axios.get(apiUrl + `editalFull/${activeCourseId}`),
          axios.get(apiUrl + 'studyPlan', { params: { courseId: activeCourseId } })
        ]);

        const editalSubjects = editalResponse.data.subjects || [];
        setSubjects(editalSubjects);

        const priorityBySubjectId = {};
        editalSubjects.forEach((s) => {
          priorityBySubjectId[s.subjectId] = 'media';
        });

        const existingPlan = planResponse.data.plan;
        if (existingPlan) {
          setHoursPerDay(existingPlan.hoursPerDay);
          setSelectedDays(existingPlan.studyDays);
          const keptIds = new Set(existingPlan.subjects.map((s) => s.subjectId));
          existingPlan.subjects.forEach((s) => {
            priorityBySubjectId[s.subjectId] = s.priority;
          });
          setRemovedSubjectIds(
            new Set(editalSubjects.filter((s) => !keptIds.has(s.subjectId)).map((s) => s.subjectId))
          );
        } else {
          setRemovedSubjectIds(new Set());
        }
        setPriorities(priorityBySubjectId);
      } catch (error) {
        console.error(error);
        alert('Erro ao carregar o edital do concurso.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeCourseId, loadingCourses]);

  // Catálogo de matérias/tópicos globais (de outros concursos), usado só nos
  // concursos personalizados pra sugerir no autocomplete de "Adicionar
  // matéria/assunto" em vez de forçar o usuário a digitar tudo do zero.
  useEffect(() => {
    if (!activeCourse?.isCustom) return;
    axios
      .get(apiUrl + 'subjectsCatalog')
      .then((response) => setCatalogSubjects(response.data.subjects || []))
      .catch((error) => console.error('Erro ao buscar catálogo de matérias:', error));
  }, [activeCourse?.isCustom]);

  // Prévia da grade semanal (sem persistir nada), recalculada ~600ms depois
  // da última mudança em horas/dias/matérias mantidas — dá uma sensação "ao
  // vivo" sem duplicar o algoritmo de distribuição (backend/studyScheduler.js)
  // em JS no frontend.
  useEffect(() => {
    if (loading || selectedDays.length === 0) {
      setPreviewPlan(null);
      return;
    }

    const chosenSubjects = subjects
      .filter((s) => !removedSubjectIds.has(s.subjectId))
      .map((s) => ({ subjectId: s.subjectId, priority: priorities[s.subjectId] || 'media' }));

    if (chosenSubjects.length === 0) {
      setPreviewPlan(null);
      return;
    }

    setPreviewLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const response = await axios.post(apiUrl + 'studyPlan/preview', {
          courseId: activeCourseId,
          hoursPerDay,
          studyDays: selectedDays,
          subjects: chosenSubjects
        });
        setPreviewPlan(response.data);
      } catch (error) {
        console.error(error);
        setPreviewPlan(null);
      } finally {
        setPreviewLoading(false);
      }
    }, 600);

    return () => {
      clearTimeout(timeoutId);
      setPreviewLoading(false);
    };
  }, [loading, subjects, removedSubjectIds, priorities, hoursPerDay, selectedDays, activeCourseId]);

  const toggleExpanded = (subjectId) => {
    setExpandedSubjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(subjectId)) {
        next.delete(subjectId);
      } else {
        next.add(subjectId);
      }
      return next;
    });
  };

  const toggleSubjectRemoved = (subjectId) => {
    setRemovedSubjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(subjectId)) {
        next.delete(subjectId);
      } else {
        next.add(subjectId);
      }
      return next;
    });
  };

  const toggleDay = (dayKey) => {
    setSelectedDays((prev) =>
      prev.includes(dayKey) ? prev.filter((d) => d !== dayKey) : [...prev, dayKey]
    );
  };

  const setSubjectPriority = (subjectId, priority) => {
    setPriorities((prev) => ({ ...prev, [subjectId]: priority }));
  };

  const handleAddCustomSubject = async (event) => {
    event.preventDefault();
    const name = newSubjectName.trim();
    if (!name) return;

    if (subjects.some((s) => s.name.toLowerCase() === name.toLowerCase())) {
      setAddSubjectError('Essa matéria já está no seu concurso.');
      return;
    }

    setAddingSubject(true);
    setAddSubjectError('');
    try {
      // Se o nome digitado bate com uma matéria já existente no catálogo
      // (autocomplete via <datalist>), vincula ela em vez de criar uma nova
      // do zero — assim os tópicos sugeridos dela ficam disponíveis a seguir.
      const catalogMatch = catalogSubjects.find((s) => s.name.toLowerCase() === name.toLowerCase());

      const response = catalogMatch
        ? await axios.post(apiUrl + `myCourses/${activeCourseId}/subjects/fromCatalog`, {
            subjectId: catalogMatch.id
          })
        : await axios.post(apiUrl + `myCourses/${activeCourseId}/subjects`, { name });

      setSubjects((prev) =>
        [
          ...prev,
          {
            subjectId: response.data.subjectId,
            courseSubjectId: response.data.courseSubjectId,
            name: response.data.name || name,
            isCustom: true,
            topics: []
          }
        ].sort((a, b) => a.name.localeCompare(b.name))
      );
      setPriorities((prev) => ({ ...prev, [response.data.subjectId]: 'media' }));
      setNewSubjectName('');
    } catch (error) {
      setAddSubjectError(error.response?.data?.message || 'Erro ao adicionar matéria.');
    } finally {
      setAddingSubject(false);
    }
  };

  const toggleTopicExcluded = async (subject, topic) => {
    if (topic.isCustom) {
      if (!window.confirm('Excluir esse assunto personalizado?')) return;
      try {
        await axios.delete(apiUrl + `myTopics/${topic.topicId}`);
        setSubjects((prev) =>
          prev.map((s) =>
            s.subjectId === subject.subjectId
              ? { ...s, topics: s.topics.filter((t) => t.topicId !== topic.topicId) }
              : s
          )
        );
      } catch (error) {
        console.error(error);
        alert('Erro ao excluir o tópico.');
      }
      return;
    }

    const excluding = !topic.isExcluded;
    try {
      if (excluding) {
        await axios.post(apiUrl + 'excludedTopics', { topicId: topic.topicId });
      } else {
        await axios.delete(apiUrl + `excludedTopics/${topic.topicId}`);
      }
      setSubjects((prev) =>
        prev.map((s) =>
          s.subjectId === subject.subjectId
            ? {
                ...s,
                topics: s.topics.map((t) =>
                  t.topicId === topic.topicId ? { ...t, isExcluded: excluding } : t
                )
              }
            : s
        )
      );
    } catch (error) {
      console.error(error);
      alert('Erro ao atualizar o tópico.');
    }
  };

  const handleAddCustomTopic = async (event, subject) => {
    event.preventDefault();
    if (!newTopicName.trim()) return;
    setAddingTopic(true);
    setAddTopicError('');
    try {
      const response = await axios.post(apiUrl + 'myTopics', {
        course_subject_id: subject.courseSubjectId,
        name: newTopicName.trim()
      });
      const newTopic = { topicId: response.data.id, name: newTopicName.trim(), isCustom: true, isExcluded: false };
      setSubjects((prev) =>
        prev.map((s) =>
          s.subjectId === subject.subjectId
            ? { ...s, topics: [...s.topics, newTopic].sort((a, b) => a.name.localeCompare(b.name)) }
            : s
        )
      );
      setNewTopicName('');
      setAddTopicFormSubjectId(null);
    } catch (error) {
      setAddTopicError(error.response?.data?.message || 'Erro ao adicionar tópico.');
    } finally {
      setAddingTopic(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (selectedDays.length === 0) {
      setErrorMessage('Selecione ao menos um dia da semana.');
      return;
    }

    const chosenSubjects = subjects
      .filter((s) => !removedSubjectIds.has(s.subjectId))
      .map((s) => ({ subjectId: s.subjectId, priority: priorities[s.subjectId] || 'media' }));

    if (chosenSubjects.length === 0) {
      setErrorMessage('Mantenha ao menos uma matéria no seu plano.');
      return;
    }

    setSaving(true);
    try {
      await axios.post(apiUrl + 'studyPlan/generate', {
        courseId: activeCourseId,
        hoursPerDay,
        studyDays: selectedDays,
        subjects: chosenSubjects
      });
      // Esse concurso passa a contar como "registrado" (hasPlan) só depois
      // desse POST — sem isso o dropdown da home (CourseSwitcher) continuaria
      // sem ele até o próximo fetchCourses (ex.: criar outro concurso).
      await refreshCourses();
      navigate('/simulados', { replace: true });
    } catch (error) {
      console.error(error);
      if (error.response?.status === 403 && error.response.data?.code === 'PLAN_LIMIT_REACHED') {
        setShowPlansModal(true);
      } else {
        setErrorMessage(error.response?.data?.message || 'Erro ao gerar o plano. Tente novamente.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="App dashboard-page subject-selection-page">
        <div className="loading-message">Carregando...</div>
      </div>
    );
  }

  if (!activeCourseId) {
    return (
      <div className="App dashboard-page subject-selection-page">
        <DashboardTopbar label="Montar plano de estudos" hideCourseSwitcher />
        <div className="empty-message">
          Selecione um concurso na tela de simulados para escolher suas matérias.
        </div>
      </div>
    );
  }

  return (
    <div className="App dashboard-page subject-selection-page">
      {showPlansModal && (
        <PlansModal
          trackingPrefix="course_limit_wall"
          heroTitle="Seu plano grátis permite 1 concurso"
          heroSubtitle="Libere acesso completo para estudar para quantos concursos quiser, com simulados e materiais de cada um."
          onClose={() => navigate('/simulados', { replace: true })}
        />
      )}
      <DashboardTopbar label="Montar plano de estudos" hideCourseSwitcher />
      {activeCourse?.isCustom ? (
        <div className="subject-selection-course-image-wrap">
          {activeCourse?.image_url && !courseImageFailed ? (
            <img
              src={activeCourse.image_url}
              alt={`Imagem do concurso ${activeCourse.name}`}
              className="subject-selection-course-image"
              onError={() => setCourseImageFailed(true)}
            />
          ) : (
            <div className="subject-selection-course-image subject-selection-course-image--placeholder">
              <SchoolOutlined style={{ fontSize: 40 }} />
            </div>
          )}
          <button
            type="button"
            className="subject-selection-course-image-edit-btn"
            aria-label="Alterar imagem do concurso"
            onClick={() => setEditingCourseImageUrl((prev) => !prev)}
          >
            <ImageOutlined style={{ fontSize: 14 }} />
          </button>
        </div>
      ) : (
        activeCourse?.image_url && !courseImageFailed ? (
          <img
            src={activeCourse.image_url}
            alt={`Imagem do concurso ${activeCourse.name}`}
            className="subject-selection-course-image"
            onError={() => setCourseImageFailed(true)}
          />
        ) : (
          <div className="subject-selection-course-image subject-selection-course-image--placeholder">
            <SchoolOutlined style={{ fontSize: 40 }} />
          </div>
        )
      )}
      {Boolean(activeCourse?.isCustom) && editingCourseImageUrl && (
        <input
          type="url"
          className="subject-selection-course-image-url-input"
          value={courseImageUrlDraft}
          onChange={(e) => setCourseImageUrlDraft(e.target.value)}
          onBlur={saveCourseImageUrl}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              setCourseImageUrlDraft(activeCourse?.image_url || '');
              setEditingCourseImageUrl(false);
            }
          }}
          placeholder="Link da imagem (opcional)"
          aria-label="Link da imagem do concurso"
          autoFocus
        />
      )}
      {activeCourse?.isCustom ? (
        <div className="subject-selection-course-name-wrap">
          <input
            type="text"
            className="subject-selection-course-name"
            value={courseNameDraft}
            onChange={(e) => setCourseNameDraft(e.target.value)}
            onBlur={saveCourseName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
            maxLength={255}
            placeholder="Nome do concurso"
            aria-label="Nome do concurso"
          />
          <EditOutlined className="subject-selection-course-name-edit-icon" style={{ fontSize: 14 }} />
        </div>
      ) : (
        activeCourse?.name && <span className="subject-selection-course-name">{activeCourse.name}</span>
      )}

      {activeCourse?.isCustom && (
        <div className="subject-selection-course-exam-date-wrap">
          <label htmlFor="subject-selection-course-exam-date">Data da prova</label>
          <input
            id="subject-selection-course-exam-date"
            type="date"
            className="subject-selection-course-exam-date"
            value={courseExamDateDraft}
            onChange={(e) => setCourseExamDateDraft(e.target.value)}
            onBlur={saveCourseExamDate}
          />
        </div>
      )}

      <h2 className="subject-selection-title">
        <CalendarMonthOutlined style={{ fontSize: 22, verticalAlign: 'middle', marginRight: 8 }} />
        Escolha suas matérias
      </h2>
      <p className="subject-selection-subtitle">
        Escolha as matérias do edital que você quer estudar. Clique numa matéria para ver os tópicos.
      </p>

      <form className="subject-selection-form" onSubmit={handleSubmit}>
        <div className="subject-selection-list">
          {subjects.map((subject, index) => {
            const color = SUBJECT_COLOR_PALETTE[index % SUBJECT_COLOR_PALETTE.length].border;
            const isRemoved = removedSubjectIds.has(subject.subjectId);
            const isExpanded = expandedSubjectIds.has(subject.subjectId);
            const activeTopicsCount = subject.topics.filter((t) => !t.isExcluded).length;

            return (
              <div
                key={subject.subjectId}
                className={`subject-selection-card ${isRemoved ? 'is-removed' : ''}`}
              >
                <div
                  className="subject-selection-card-header"
                  onClick={() => !isRemoved && toggleExpanded(subject.subjectId)}
                  role="button"
                  tabIndex={0}
                >
                  <span className="subject-selection-stripe" style={{ backgroundColor: color }} />
                  <div className="subject-selection-card-body">
                    <span className="subject-selection-card-name">{subject.name}</span>
                    <span className="subject-selection-card-meta">
                      {isRemoved
                        ? 'Removida do seu plano'
                        : `${activeTopicsCount} tópico${activeTopicsCount === 1 ? '' : 's'}`}
                    </span>
                    {!isRemoved && (
                      <div
                        className="subject-selection-priority-selector"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="subject-selection-priority-label">Prioridade de estudo</span>
                        {PRIORITIES.map((priority) => {
                          const filledBars = PRIORITY_BAR_COUNT[priority];
                          return (
                            <button
                              type="button"
                              key={priority}
                              className={`subject-selection-priority-btn ${priority} ${
                                (priorities[subject.subjectId] || 'media') === priority ? 'active' : ''
                              }`}
                              onClick={() => setSubjectPriority(subject.subjectId, priority)}
                            >
                              <span className="subject-selection-priority-bars" aria-hidden="true">
                                {[1, 2, 3].map((bar) => (
                                  <i key={bar} className={bar <= filledBars ? 'filled' : ''} />
                                ))}
                              </span>
                              {PRIORITY_LABELS[priority]}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {!isRemoved && (
                    isExpanded
                      ? <ExpandLessOutlined style={{ fontSize: 18, flexShrink: 0, color: 'var(--text-secondary)' }} />
                      : <ExpandMoreOutlined style={{ fontSize: 18, flexShrink: 0, color: 'var(--text-secondary)' }} />
                  )}
                  <button
                    type="button"
                    className={`subject-selection-remove-btn ${isRemoved ? 'is-restore' : ''}`}
                    aria-label={isRemoved ? `Readicionar ${subject.name}` : `Remover ${subject.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSubjectRemoved(subject.subjectId);
                    }}
                  >
                    {isRemoved
                      ? <ReplayOutlined style={{ fontSize: 16 }} />
                      : <CloseOutlined style={{ fontSize: 16 }} />}
                  </button>
                </div>

                {!isRemoved && isExpanded && (
                  <div className="subject-selection-topics">
                    {subject.topics.map((topic) => (
                      <span
                        key={topic.topicId}
                        className={`subject-selection-topic-chip ${topic.isExcluded ? 'is-excluded' : ''}`}
                      >
                        {topic.name}
                        {topic.isCustom && <em> (personalizado)</em>}
                        <button
                          type="button"
                          aria-label={
                            topic.isCustom
                              ? `Excluir assunto ${topic.name}`
                              : topic.isExcluded
                                ? `Readicionar assunto ${topic.name}`
                                : `Remover assunto ${topic.name}`
                          }
                          onClick={() => toggleTopicExcluded(subject, topic)}
                        >
                          {topic.isCustom
                            ? <DeleteOutlineOutlined style={{ fontSize: 14 }} />
                            : topic.isExcluded
                              ? <ReplayOutlined style={{ fontSize: 14 }} />
                              : <CloseOutlined style={{ fontSize: 14 }} />}
                        </button>
                      </span>
                    ))}

                    {addTopicFormSubjectId === subject.subjectId ? (
                      <div className="subject-selection-add-topic">
                        <AutocompleteField
                          value={newTopicName}
                          onChange={setNewTopicName}
                          onSelectOption={setNewTopicName}
                          options={(catalogSubjects.find((s) => s.id === subject.subjectId)?.topics || [])
                            .filter((name) => !subject.topics.some((t) => t.name.toLowerCase() === name.toLowerCase()))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAddCustomTopic(e, subject);
                          }}
                          placeholder="Assunto (existente ou novo)"
                          maxLength={255}
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={(e) => handleAddCustomTopic(e, subject)}
                          disabled={addingTopic || !newTopicName.trim()}
                        >
                          {addingTopic ? 'Adicionando...' : 'Adicionar'}
                        </button>
                        <button
                          type="button"
                          className="subject-selection-add-topic-cancel"
                          onClick={() => {
                            setAddTopicFormSubjectId(null);
                            setNewTopicName('');
                            setAddTopicError('');
                          }}
                        >
                          Cancelar
                        </button>
                        {addTopicError && <span className="subject-selection-add-topic-error">{addTopicError}</span>}
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="subject-selection-add-topic-trigger"
                        onClick={() => setAddTopicFormSubjectId(subject.subjectId)}
                      >
                        <AddOutlined style={{ fontSize: 14, verticalAlign: 'middle', marginRight: 2 }} />
                        Adicionar tópico
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {Boolean(activeCourse?.isCustom) && (
          <div className="subject-selection-add-subject">
            <AutocompleteField
              value={newSubjectName}
              onChange={setNewSubjectName}
              onSelectOption={setNewSubjectName}
              options={catalogSubjects
                .filter((s) => !subjects.some((added) => added.name.toLowerCase() === s.name.toLowerCase()))
                .map((s) => s.name)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCustomSubject(e);
                }
              }}
              placeholder="Nome da matéria (existente ou nova)"
              maxLength={255}
            />
            <button type="button" onClick={handleAddCustomSubject} disabled={addingSubject || !newSubjectName.trim()}>
              <AddOutlined style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />
              {addingSubject ? 'Adicionando...' : 'Adicionar matéria'}
            </button>
            {addSubjectError && <span className="subject-selection-add-subject-error">{addSubjectError}</span>}
          </div>
        )}

        <div className="subject-selection-field">
          <label>Quantas horas por dia você consegue estudar?</label>
          <input
            type="number"
            min="0.5"
            max="24"
            step="0.5"
            value={hoursPerDay}
            onChange={(e) => {
              const value = Number(e.target.value);
              setHoursPerDay(Number.isFinite(value) ? Math.min(24, Math.max(0, value)) : 0);
            }}
          />
        </div>

        <div className="subject-selection-field">
          <label>Quais dias da semana pretende estudar?</label>
          <div className="subject-selection-days">
            {DAYS.map((day) => (
              <button
                type="button"
                key={day.key}
                className={`subject-selection-day-chip ${selectedDays.includes(day.key) ? 'active' : ''}`}
                onClick={() => toggleDay(day.key)}
              >
                {day.label}
              </button>
            ))}
          </div>
        </div>

        {previewLoading && (
          <div className="subject-selection-preview-loading">Montando sua grade de horários...</div>
        )}
        {!previewLoading && previewPlan && (
          <div className="subject-selection-preview">
            <span className="subject-selection-preview-label">Prévia da sua grade semanal</span>
            <StudyPlanCalendar plan={previewPlan} />
          </div>
        )}

        {errorMessage && <div className="subject-selection-error">{errorMessage}</div>}

        <button type="submit" className="subject-selection-submit" disabled={saving}>
          {saving ? 'Gerando...' : 'Confirmar e começar a estudar'}
        </button>
      </form>
    </div>
  );
}

export default SubjectSelection;
